import type { Swatch, SwatchType } from "replo-runtime/shared/types";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import SelectionIndicator from "@v1/components/common/designSystem/SelectionIndicator";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import SearcheableList from "@v1/components/editor/page/element-editor/components/SearcheableList";
import SwatchPopover from "@v1/components/editor/page/SwatchPopover";
import {
  selectDraftComponentType,
  selectProjectId,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { swatchRouter } from "@v1/trpc/routes/swatch";
import { Palette, Plus, SlidersVertical } from "lucide-react";
import { fakeSwatches } from "replo-runtime/store/utils/fakeSwatches";
import { v4 as uuidv4 } from "uuid";

type SwatchToggleOption = "swatches" | "placeholders";

export const SwatchesCustomPropModifier: React.FC = () => {
  const projectId = useEditorSelector(selectProjectId);
  const { data: swatches = [] } = swatchRouter.list.useQuery(
    projectId ? { projectId } : skipToken,
  );
  const [activeSwatchId, setActiveSwatchId] = React.useState<string | null>(
    null,
  );
  const componentType = useEditorSelector(selectDraftComponentType);

  const activeSwatch =
    swatches.find(({ id }) => id === activeSwatchId) ??
    fakeSwatches.find(({ id }) => id === activeSwatchId);

  const [swatchToggleValue, setSwatchToggleValue] =
    React.useState<SwatchToggleOption>("swatches");

  return (
    <ModifierGroup
      title="Swatches"
      icon={Plus}
      onClick={() => setActiveSwatchId(uuidv4())}
      hideEndEnhancerOnGroupClosed={false}
      iconTooltip="Add a swatch"
    >
      <div className="flex flex-col gap-2">
        <DeprecatedToggleGroup
          size="sm"
          options={[
            {
              label: "My Swatches",
              value: "swatches",
            },
            {
              label: "Placeholders",
              value: "placeholders",
            },
          ]}
          selectedValue={swatchToggleValue}
          onChange={(value) => {
            setSwatchToggleValue(value as SwatchToggleOption);
          }}
        />
        {swatchToggleValue === "placeholders" && (
          <SearcheableList
            title="Swatches"
            options={fakeSwatches.map(({ id, name }) => ({
              label: name,
              value: id,
              content: () => (
                <SelectionIndicator
                  title={name}
                  onClick={() => setActiveSwatchId(id)}
                  startEnhancer={
                    <Palette
                      className="roundedsm bg-primary p-0.5 text-white"
                      size={16}
                    />
                  }
                  endEnhancer={
                    <SlidersVertical
                      size={12}
                      onClick={() => setActiveSwatchId(id)}
                    />
                  }
                />
              ),
            }))}
          />
        )}
        {swatchToggleValue === "swatches" && swatches.length > 0 && (
          <SearcheableList
            title="Swatches"
            options={swatches.map(({ id, name }) => ({
              label: name,
              value: id,
              content: () => (
                <SelectionIndicator
                  title={name}
                  onClick={() => setActiveSwatchId(id)}
                  startEnhancer={
                    <Palette
                      className="bg-primary rounded-sm p-0.5 text-white"
                      size={16}
                    />
                  }
                  endEnhancer={
                    <SlidersVertical
                      size={12}
                      onClick={() => setActiveSwatchId(id)}
                    />
                  }
                />
              ),
            }))}
          />
        )}
        {swatchToggleValue === "swatches" && swatches.length === 0 && (
          <div
            className="text-muted cursor-pointer text-left text-xs"
            onClick={() => setActiveSwatchId(uuidv4())}
          >
            Click the + icon to add a swatch
          </div>
        )}
        <SwatchPopover
          swatch={
            activeSwatch ??
            createNewSwatch(
              activeSwatchId!,
              componentType === "variantSelect" ? "variant" : "option",
            )
          }
          isReadOnly={fakeSwatches.some(({ id }) => id === activeSwatchId)}
          isOpen={activeSwatchId !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setActiveSwatchId(null);
            }
          }}
          isNewSwatch={!activeSwatch}
        />
      </div>
    </ModifierGroup>
  );
};

function createNewSwatch(id: string, defaultType: SwatchType): Swatch {
  return {
    id,
    name: "",
    data: {
      type: defaultType,
      productIds: [],
      options: [],
      variants: [],
    },
  };
}
